import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { peekInvite, redeemInvite } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export function InvitePage({ onToast, onRequireAuth, onOpenLeague }) {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    peekInvite(token)
      .then((d) => { if (!cancelled) setInvite(d); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || "Invite link is invalid."); });
    return () => { cancelled = true; };
  }, [token]);

  const accept = async () => {
    if (!user) { onRequireAuth?.(); return; }
    setBusy(true);
    try {
      const r = await redeemInvite(token);
      onToast?.(r.already_member ? "✅ You're already in." : "🎉 Joined the league.");
      onOpenLeague?.(r.league_id);
      navigate("/", { replace: true });
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not redeem this invite.";
      setError(msg);
      onToast?.(msg);
    } finally { setBusy(false); }
  };

  return (
    <main className="page-z" data-testid="invite-page">
      <div className="max-w-2xl mx-auto px-6 lg:px-10 pt-16 pb-20">
        <Link to="/" data-testid="invite-back-home" className="inline-flex items-center gap-2 text-xs tracking-widest text-[#a3a3ac] hover:text-[#14b8a6]">
          <i className="fas fa-arrow-left" /> BACK TO ROADMAP
        </Link>

        <div className="mt-6 glass rounded-3xl p-8 text-center neon-glow border-2 border-[#14b8a6]/50">
          <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">🔗 PRO LEAGUE INVITE</p>

          {error ? (
            <>
              <h2 data-testid="invite-error" className="font-display text-3xl mt-3 text-[#14b8a6]">INVALID</h2>
              <p className="text-sm text-white/70 mt-3">{error}</p>
            </>
          ) : !invite ? (
            <>
              <div className="loader-ring mx-auto mt-8" />
              <p className="text-sm text-white/60 mt-4">Validating link…</p>
            </>
          ) : !invite.valid ? (
            <>
              <h2 data-testid="invite-used" className="font-display text-3xl grad-text mt-3">USED</h2>
              <p className="text-sm text-white/70 mt-3">This invite has already been redeemed.</p>
            </>
          ) : (
            <>
              {invite.banner_url && (
                <img
                  src={invite.banner_url}
                  alt="League banner"
                  data-testid="invite-banner"
                  className="rounded-2xl w-full max-h-44 object-cover mt-3 border border-[#14b8a6]/30"
                />
              )}
              <h2 data-testid="invite-league-name" className="font-display text-3xl md:text-4xl grad-text mt-4 leading-tight">
                {invite.pro_badge && <span className="mr-2">{invite.pro_badge}</span>}
                {invite.league_name}
              </h2>
              <p className="text-xs text-[#a3a3ac] mt-2">
                leaguehub.co/{invite.league_slug}
              </p>
              <p className="text-sm text-white/70 mt-4">
                You've been invited to join this <span className="text-[#14b8a6] font-bold">PRO league</span>.
                Skip the application — one click to drop in.
              </p>
              <p className="text-[11px] text-white/50 mt-2">
                {invite.spots_open} spot{invite.spots_open === 1 ? "" : "s"} open
              </p>
              <button
                onClick={accept}
                disabled={busy || invite.spots_open <= 0}
                data-testid="invite-accept-btn"
                className="mt-6 w-full btn-neon rounded-2xl py-4 text-sm tracking-[3px] disabled:opacity-40"
              >
                {busy ? "JOINING..." : invite.spots_open <= 0 ? "LEAGUE FULL" : "🎯 ACCEPT INVITE"}
              </button>
              {!user && (
                <p className="text-[11px] text-white/50 mt-3">
                  Sign-in required — we'll bring you back here right after.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </main>
  );
}

export default InvitePage;
